import * as z from 'zod/v4';
import { ImageEnums, ImageType, RewardItemEnums, StringEnum } from './types';
import { EnumParams } from '../events.types';

export const colorSchema = z.string().refine(
  val => {
    const hexRegex = /^#([0-9a-fA-F]{6})$/;
    return hexRegex.test(val);
  },
  {
    message: 'Invalid color format. Please use a valid hex color code (e.g., #000000).',
  },
);

export const IMAGE_TYPE_VALUES = ['resource', 'gear', 'hero', 'path'] as const;
export const JIGGLE_TYPE_VALUES = ['None', 'Always', 'UntilPreviewed', 'WithRedDot'] as const;

export const simpleConfigSchema = (enums?: EnumParams, withIsPity: boolean = true) =>
  z.object({
    drops: z
      .array(
        z.object({
          odds: z.number().nonnegative(),
          reward: rewardSchema(enums),
          ...(withIsPity && { isPity: z.boolean() }),
        }),
      )
      .refine(
        data => {
          if (!withIsPity) {
            return true;
          }
          const hasOnePity = data.filter(({ isPity }) => isPity).length === 1;

          if (!hasOnePity) {
            return false;
          }
          return true;
        },
        { message: 'Should have exactly one pity drop' },
      ),
  });

export const tsvConfigSchema = (enums?: EnumParams, withIsPity: boolean = true) =>
  z.union([
    z.object({
      dropTableConfig: z.string(),
    }),
    z.object({
      simpleConfig: simpleConfigSchema(enums, withIsPity),
    }),
  ]);

export const imageSchema: (enums?: ImageEnums) => z.ZodType<{ type: ImageType; value: string }> = (
  enums?: ImageEnums,
) =>
  z.discriminatedUnion('type', [
    z.object({
      type: z.literal(IMAGE_TYPE_VALUES[0]),
      value: enums ? z.enum(enums.ResourceType) : z.string(),
    }),
    z.object({
      type: z.literal(IMAGE_TYPE_VALUES[1]),
      value: enums ? z.enum(enums.GearType) : z.string(),
    }),
    z.object({
      type: z.literal(IMAGE_TYPE_VALUES[2]),
      value: enums ? z.enum(enums.HeroType) : z.string(),
    }),
    z.object({
      type: z.literal(IMAGE_TYPE_VALUES[3]),
      value: z.string(),
    }),
  ]);

export const LOCALIZED_STRING_TYPE_VALUES = ['image', 'inline'] as const;

// unused for now
const _localizedBundleSchema = z.object({
  type: z.literal('bundle'),
  bundleName: z.string(),
  bundleKey: z.string(),
});

export const localizedStringSchema = z.object({
  type: z.enum(LOCALIZED_STRING_TYPE_VALUES),
  id: z.string(),
  title: z.string(),
  values: z.record(z.string(), z.string()),
});

export const ITEM_ENUM = ['resource', 'hero', 'gear', 'box'] as const;

const resourceRewardSchema = (items?: StringEnum) =>
  z.object({
    itemCategory: z.literal(ITEM_ENUM[0]),
    itemType: items ? z.enum(items) : z.string(),
  });

const heroRewardSchema = (items?: StringEnum) =>
  z.object({
    itemCategory: z.literal(ITEM_ENUM[1]),
    itemType: items ? z.enum(items) : z.string(),
  });

const gearRewardSchema = (items?: StringEnum) =>
  z.object({
    itemCategory: z.literal(ITEM_ENUM[2]),
    itemType: items ? z.enum(items) : z.string(),
  });

const boxRewardSchema = (items?: StringEnum) =>
  z.object({
    itemCategory: z.literal(ITEM_ENUM[3]),
    itemType: items ? z.enum(items) : z.string(),
  });

export const rewardItemSchema = (enums?: RewardItemEnums) =>
  z.discriminatedUnion('itemCategory', [
    resourceRewardSchema(enums?.ResourceType),
    heroRewardSchema(enums?.HeroType),
    gearRewardSchema(enums?.GearType),
    boxRewardSchema(enums?.BoxType),
  ]);

export const rewardSchema = (enums?: RewardItemEnums) =>
  rewardItemSchema(enums)
    .and(
      z.object({
        featured: z.boolean().optional(),
        quantity: z.number().int().nonnegative(),
        extraInt: z.number().int().optional(),
        extraString: z.string().optional(),
        buttonHighlight: z.boolean().optional(),
      }),
    )
    .superRefine(({ itemCategory, extraString }, ctx) => {
      if (itemCategory === 'box' && !extraString) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'extraString is required when itemCategory is box',
          path: ['extraString'],
        });
        return;
      }
    });

export const rewardMessageSchema = (enums?: ImageEnums & { MailType: StringEnum }) =>
  z.object({
    sender: localizedStringSchema.optional(),
    type: enums ? z.enum(enums.MailType) : z.string(),
    icon: imageSchema(enums).optional(),
    subject: localizedStringSchema.optional(),
    body: localizedStringSchema.optional(),
  });

export const TASK_TYPE_COUNTER = 'counter' as const;
export const TASK_TYPE_LEADERBOARD = 'leaderboard' as const;

export const leaderboardTaskItemSchema = (enums?: EnumParams) =>
  z.object({
    id: z.number().int().positive().optional(),
    type: enums ? z.enum(enums.EventLeaderboardTaskType) : z.string(),
    icon: imageSchema(enums).optional(),
    data: z.object({
      hero: (enums ? z.enum(enums.HeroType) : z.string()).optional(),
      resource: (enums ? z.enum(enums.ResourceType) : z.string()).optional(),
      rarity: (enums ? z.enum(enums.Rarity) : z.string()).optional(),
      gameMode: (enums ? z.enum(enums.GameMode) : z.string()).optional(),
      chestType: (enums ? z.enum(enums.ChestType) : z.string()).optional(),
      combatTag: (enums ? z.enum(enums.CombatTag) : z.string()).optional(),
      enemy: (enums ? z.enum(enums.EnemyType) : z.string()).optional(),
    }),
    description: localizedStringSchema,
    target: z.number().nonnegative(),
    pointsEarned: z.number().nonnegative(),
    maxCompletions: z.number().nonnegative().optional(),
    maxDailyCompletions: z.number().nonnegative().optional(),
    startOffset: z.iso.duration().optional(),
    duration: z.iso.duration().optional(),
    buttonAction: z.url().optional(),
    backfill: z.boolean().optional().default(true),
  });

export const eventTaskSchema = (enums?: EnumParams) =>
  z
    .object({
      taskType: enums ? z.enum(enums?.EventTaskType) : z.string(),
      taskCounterType: (enums ? z.enum(enums?.CounterType) : z.string()).optional(),
      image: imageSchema(enums).optional(),
      description: localizedStringSchema.optional(),
      leaderboardTask: leaderboardTaskItemSchema(enums).optional(),
    })
    .superRefine((data, ctx) => {
      if (data.taskType === TASK_TYPE_COUNTER && !data.taskCounterType) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'taskCounterType is required when taskType is counter',
          path: ['taskCounterType'],
        });
      }
    });

export const sparseRangeSchema = z.object({
  ranges: z
    .array(
      z.object({
        min: z.number().int(),
        max: z.number().int(),
      }),
    )
    .min(1),
});
